import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  TextField,
  Typography,
} from '@shared/ui';
import { useRef, useState } from 'react';
import {
  useGetCalendarChangeReviewQueueQuery,
  useResolveCalendarChangeMutation,
} from './api/adminCalendarChangesApi';

const maxResolutionNoteLength = 2000;
const resolutionNoteTooLong = (note: string) =>
  Array.from(note.trim()).length > maxResolutionNoteLength;

export function AdminCalendarChangeReviewQueue() {
  const [cursor, setCursor] = useState<string | null>(null);
  const { data, error, isLoading } = useGetCalendarChangeReviewQueueQuery({ cursor, limit: 100 });
  const [resolveChange, resolution] = useResolveCalendarChangeMutation();
  const [notes, setNotes] = useState<Record<string, string>>({});
  const pendingResolutions = useRef(new Set<string>());

  if (isLoading) return <CircularProgress size={24} />;
  if (error)
    return <Alert severity="error">Could not load the calendar change review queue.</Alert>;

  // No actor field: the server records resolved_by from the authenticated
  // admin. A typed name would be a forgeable audit trail.
  const canResolve = !resolution.isLoading;
  const submitResolution = (changeId: string, kind: 'acknowledged' | 'dismissed') => {
    if (pendingResolutions.current.has(changeId) || resolutionNoteTooLong(notes[changeId] ?? '')) {
      return;
    }
    pendingResolutions.current.add(changeId);
    void resolveChange({
      changeId,
      kind,
      note: notes[changeId]?.trim() || undefined,
    })
      .unwrap()
      .then(() =>
        setNotes((current) => {
          const remaining = { ...current };
          delete remaining[changeId];
          return remaining;
        })
      )
      .catch(() => undefined)
      .finally(() => pendingResolutions.current.delete(changeId));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography component="h2" variant="h6">
        Review queue
      </Typography>
      {resolution.error && (
        <Alert severity="error">
          {(resolution.error as { data?: { error?: string } }).data?.error === 'seat_change_in_progress'
            ? 'This member has a seat change in progress. Resolve the seat change first.'
            : 'Could not resolve the calendar change.'}
        </Alert>
      )}
      {data?.changes.length === 0 && <Typography>Nothing needs review.</Typography>}
      {data?.changes.map((change) => (
        <Card key={change.id} variant="outlined">
          <CardContent>
            <Typography color="error.main" fontWeight={700}>
              {change.reason}
            </Typography>
            <Typography color="text.secondary" variant="body2">
              {change.coach_person_id} | {change.scope} |{' '}
              {new Date(change.detected_at).toLocaleString()}
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '2fr auto auto' },
                gap: 1.5,
                mt: 2,
                alignItems: 'center',
              }}
            >
              <TextField
                error={resolutionNoteTooLong(notes[change.id] ?? '')}
                helperText={
                  resolutionNoteTooLong(notes[change.id] ?? '')
                    ? `Note must be ${maxResolutionNoteLength} characters or fewer.`
                    : undefined
                }
                label="Note"
                minRows={2}
                multiline
                value={notes[change.id] ?? ''}
                onChange={(event) =>
                  setNotes((current) => ({ ...current, [change.id]: event.target.value }))
                }
                size="small"
              />
              <Button
                disabled={!canResolve || resolutionNoteTooLong(notes[change.id] ?? '')}
                onClick={() => submitResolution(change.id, 'acknowledged')}
                variant="contained"
              >
                Acknowledge
              </Button>
              <Button
                disabled={!canResolve || resolutionNoteTooLong(notes[change.id] ?? '')}
                onClick={() => submitResolution(change.id, 'dismissed')}
                variant="outlined"
              >
                Dismiss
              </Button>
            </Box>
          </CardContent>
        </Card>
      ))}
      {data?.next_cursor && (
        <Button
          onClick={() => setCursor(data.next_cursor)}
          sx={{ alignSelf: 'flex-end' }}
          variant="outlined"
        >
          Next review page
        </Button>
      )}
    </Box>
  );
}
